/**
 * Cache condivisa per GET /client-config.
 * Una sola richiesta per sessione; tutte le istanze (ProductImage, LoginContext, ecc.) riusano il risultato.
 */
let cachedPromise = null;
let cachedConfig = null;

function buildEnvFallbackConfig() {
  // CRA inlines REACT_APP_* at build time; window.* is a runtime escape hatch (optional)
  const w = typeof window !== 'undefined' ? window : {};
  const env = typeof process !== 'undefined' ? process.env : {};

  return {
    REACT_APP_API_BASE_URL: w.REACT_APP_API_BASE_URL || env.REACT_APP_API_BASE_URL,
    REACT_APP_API_EMAIL: w.REACT_APP_API_EMAIL || env.REACT_APP_API_EMAIL,
    REACT_APP_API_PASSWORD: w.REACT_APP_API_PASSWORD || env.REACT_APP_API_PASSWORD,
    REACT_APP_DEV_BACKEND_URL: w.REACT_APP_DEV_BACKEND_URL || env.REACT_APP_DEV_BACKEND_URL,
    REACT_APP_PROD_BACKEND_URL: w.REACT_APP_PROD_BACKEND_URL || env.REACT_APP_PROD_BACKEND_URL,
    REACT_APP_USE_SPACES_STORAGE:
      w.REACT_APP_USE_SPACES_STORAGE ?? (env.REACT_APP_USE_SPACES_STORAGE === 'true'),
    REACT_APP_DO_SPACES_ENDPOINT: w.REACT_APP_DO_SPACES_ENDPOINT || env.REACT_APP_DO_SPACES_ENDPOINT,
    REACT_APP_DO_SPACES_KEY: w.REACT_APP_DO_SPACES_KEY || env.REACT_APP_DO_SPACES_KEY,
    REACT_APP_DO_SPACES_SECRET: w.REACT_APP_DO_SPACES_SECRET || env.REACT_APP_DO_SPACES_SECRET,
    REACT_APP_DO_SPACES_BUCKET: w.REACT_APP_DO_SPACES_BUCKET || env.REACT_APP_DO_SPACES_BUCKET,
    REACT_APP_DO_SPACES_URL: w.REACT_APP_DO_SPACES_URL || env.REACT_APP_DO_SPACES_URL,
    REACT_APP_DO_SPACES_CDN_URL: w.REACT_APP_DO_SPACES_CDN_URL || env.REACT_APP_DO_SPACES_CDN_URL,
    REACT_APP_AZIENDA_RAGIONE_SOCIALE:
      w.REACT_APP_AZIENDA_RAGIONE_SOCIALE || env.REACT_APP_AZIENDA_RAGIONE_SOCIALE,
    REACT_APP_AZIENDA_INDIRIZZO: w.REACT_APP_AZIENDA_INDIRIZZO || env.REACT_APP_AZIENDA_INDIRIZZO,
    REACT_APP_AZIENDA_CAP: w.REACT_APP_AZIENDA_CAP || env.REACT_APP_AZIENDA_CAP,
    REACT_APP_AZIENDA_COMUNE: w.REACT_APP_AZIENDA_COMUNE || env.REACT_APP_AZIENDA_COMUNE,
    REACT_APP_AZIENDA_PROVINCIA: w.REACT_APP_AZIENDA_PROVINCIA || env.REACT_APP_AZIENDA_PROVINCIA,
    REACT_APP_AZIENDA_PARTITA_IVA: w.REACT_APP_AZIENDA_PARTITA_IVA || env.REACT_APP_AZIENDA_PARTITA_IVA,
    REACT_APP_AZIENDA_CODICE_FISCALE: w.REACT_APP_AZIENDA_CODICE_FISCALE || env.REACT_APP_AZIENDA_CODICE_FISCALE,
    REACT_APP_AZIENDA_EMAIL_CONTATTO: w.REACT_APP_AZIENDA_EMAIL_CONTATTO || env.REACT_APP_AZIENDA_EMAIL_CONTATTO,
    REACT_APP_AZIENDA_NUMERO_TELEFONO:
      w.REACT_APP_AZIENDA_NUMERO_TELEFONO || env.REACT_APP_AZIENDA_NUMERO_TELEFONO,
  };
}

/**
 * Restituisce la config client (configurazione azienda). Una sola GET /client-config.
 * @returns {Promise<Object|null>} config (o null se errore)
 */
export function getClientConfig() {
  if (cachedConfig !== null) {
    return Promise.resolve(cachedConfig);
  }
  if (cachedPromise === null) {
    cachedPromise = fetch('/client-config')
      .then(async (res) => {
        // In modalità "frontend only" (senza server Express multi-tenant), /client-config può non esistere.
        // In quel caso usiamo le variabili REACT_APP_* come fallback.
        if (!res.ok) {
          if (res.status === 404) {
            const fallback = buildEnvFallbackConfig();
            cachedConfig = fallback;
            return cachedConfig;
          }
          throw new Error(`HTTP ${res.status}`);
        }
        return await res.json();
      })
      .then((data) => {
        // Se è già una config (fallback), la restituiamo così com'è.
        if (data && typeof data === 'object' && !('success' in data) && !('config' in data)) {
          cachedConfig = data;
          return cachedConfig;
        }
        if (data.success && data.config) {
          cachedConfig = data.config;
          return cachedConfig;
        }
        // Se risposta non nel formato atteso, prova comunque fallback
        const fallback = buildEnvFallbackConfig();
        cachedConfig = fallback;
        return cachedConfig;
      })
      .catch((err) => {
        console.error('[clientConfigCache] Errore GET /client-config:', err);
        // Fallback su env per non bloccare l'app
        const fallback = buildEnvFallbackConfig();
        cachedConfig = fallback;
        return cachedConfig;
      });
  }
  return cachedPromise;
}

/**
 * Invalida la cache (utile dopo cambio tenant/login). Le prossime chiamate a getClientConfig() rifaranno la GET.
 */
export function clearClientConfigCache() {
  cachedPromise = null;
  cachedConfig = null;
}
